import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import TableChartIcon from "@mui/icons-material/TableChart";
import { API_BASE_URL } from "../config/api";
import {
  loadLiqIntelRecentlyViewed,
  rememberLiqIntelView,
  type LiqIntelRecentView,
} from "../utils/liqIntelRecentlyViewed";
import "./LiqIntel.css";

type AnalyzeProvider = "openai" | "perplexity" | "gemini";

type BankSearchRow = {
  name: string;
  city: string;
  state: string;
  cert: number;
};

/** Match CERT from decorated bank line or bare digits (same spirit as backend FDIC/CERT parsing). */
function extractCertFromCompanyInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^\d{1,10}$/.test(trimmed)) return trimmed;
  const certLabel = trimmed.match(/\bCERT\D*(\d{1,10})\b/i);
  if (certLabel?.[1]) return certLabel[1];
  const certEq = trimmed.match(/\bcert\D*=\D*(\d{1,10})\b/i);
  if (certEq?.[1]) return certEq[1];
  return null;
}

function LiqIntel() {
  const [companyName, setCompanyName] = useState("");
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);
  const [bankRows, setBankRows] = useState<BankSearchRow[]>([]);
  const [pendingAnalyzeProvider, setPendingAnalyzeProvider] =
    useState<AnalyzeProvider | null>(null);
  const [analyzeResolving, setAnalyzeResolving] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState<LiqIntelRecentView[]>([]);

  useEffect(() => {
    setRecentlyViewed(loadLiqIntelRecentlyViewed());
  }, []);

  const openReport = (provider: AnalyzeProvider, nameOverride?: string, cert?: number) => {
    const q = (nameOverride ?? companyName).trim();
    if (!q) return;
    const certPart = Number.isFinite(cert) ? `&cert=${encodeURIComponent(String(cert))}` : "";
    const url = `/liqintel-report?company=${encodeURIComponent(q)}&provider=${provider}${certPart}`;
    setRecentlyViewed(
      rememberLiqIntelView({
        company: q,
        provider,
        ...(Number.isFinite(cert) ? { cert } : {}),
      })
    );
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openDelinquencyTab = () => {
    const q = companyName.trim();
    if (!q) return;
    const cert = extractCertFromCompanyInput(q);
    const certPart = cert != null ? `&cert=${encodeURIComponent(cert)}` : "";
    const url = `/liqintel-delinquency?company=${encodeURIComponent(q)}${certPart}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const disabled = !companyName.trim() || analyzeResolving;
  const delinqTabDisabled = !companyName.trim() || analyzeResolving;

  const fetchBankMatches = async (q: string): Promise<BankSearchRow[]> => {
    const url = `${API_BASE_URL}/api/banks/search?q=${encodeURIComponent(q)}`;
    const r = await fetch(url);
    const data: unknown = await r.json().catch(() => ({}));
    if (!r.ok) {
      const errMsg =
        typeof data === "object" &&
        data !== null &&
        "error" in data &&
        typeof (data as { error: unknown }).error === "string"
          ? (data as { error: string }).error
          : r.statusText || "Search failed";
      throw new Error(errMsg);
    }
    return typeof data === "object" &&
      data !== null &&
      "banks" in data &&
      Array.isArray((data as { banks: unknown }).banks)
      ? (data as { banks: BankSearchRow[] }).banks
      : [];
  };

  const runBankSearch = async () => {
    const q = companyName.trim();
    if (!q) return;
    setPendingAnalyzeProvider(null);
    setBankDialogOpen(true);
    setBankLoading(true);
    setBankError(null);
    setBankRows([]);
    try {
      setBankRows(await fetchBankMatches(q));
    } catch (e) {
      setBankError(e instanceof Error ? e.message : "Search failed");
    } finally {
      setBankLoading(false);
    }
  };

  const resolveAndAnalyze = async (provider: AnalyzeProvider) => {
    const q = companyName.trim();
    if (!q) return;
    setAnalyzeResolving(true);
    setBankError(null);
    try {
      const matches = await fetchBankMatches(q);
      if (matches.length === 0) {
        openReport(provider, q);
        return;
      }
      if (matches.length === 1) {
        const only = matches[0];
        openReport(provider, only.name, only.cert);
        return;
      }
      setPendingAnalyzeProvider(provider);
      setBankRows(matches);
      setBankDialogOpen(true);
    } catch (e) {
      setBankError(e instanceof Error ? e.message : "Search failed");
      openReport(provider, q);
    } finally {
      setAnalyzeResolving(false);
    }
  };

  const selectBank = (row: BankSearchRow) => {
    const cityState = [row.city, row.state].filter((v) => v.trim() !== "").join(", ");
    const metaParts = [cityState, `CERT ${row.cert}`].filter((v) => v.trim() !== "");
    const decoratedName =
      metaParts.length > 0 ? `${row.name} (${metaParts.join(", ")})` : row.name;
    setCompanyName(decoratedName);

    if (pendingAnalyzeProvider) {
      openReport(pendingAnalyzeProvider, row.name, row.cert);
      setPendingAnalyzeProvider(null);
      setBankDialogOpen(false);
      setBankRows([]);
      setBankError(null);
      return;
    }
    setBankDialogOpen(false);
    setBankRows([]);
    setBankError(null);
  };

  return (
    <div className="liqintel-page">
      <header className="liqintel-header">
        <img
          src="https://app.soteriamarket.com/banner3.jpeg"
          alt="Soteria Logo"
          className="liqintel-logo"
        />
        <h1>Soteria Liquidity Intelligence 1.21</h1>
        <p className="liqintel-subtitle">
          Enter a company to research. Analysis runs on our server and
          opens the report in a new tab.
        </p>
        <div className="liqintel-form">
          <label className="liqintel-label" htmlFor="liqintel-company">
            Company name
          </label>
          <div className="liqintel-input-row">
            <input
              id="liqintel-company"
              type="text"
              className="liqintel-input"
              placeholder="e.g. Ares Management Corporation"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void resolveAndAnalyze("perplexity");
              }}
              autoComplete="organization"
            />
            <div className="liqintel-input-row-icons">
              <Tooltip title="Search US banks list by name">
                <span className="liqintel-search-btn-wrap">
                  <IconButton
                    type="button"
                    color="primary"
                    onClick={() => void runBankSearch()}
                    disabled={disabled}
                    className="liqintel-search-banks-btn"
                    aria-label="Search US banks list"
                    size="medium"
                  >
                    <SearchIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Open delinquency table in a new tab (include CERT in the field for data)">
                <span className="liqintel-search-btn-wrap">
                  <IconButton
                    type="button"
                    color="primary"
                    onClick={() => openDelinquencyTab()}
                    disabled={delinqTabDisabled}
                    className="liqintel-delinq-debug-btn"
                    aria-label="Open delinquency table in new tab"
                    size="medium"
                  >
                    <TableChartIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
          <div className="liqintel-actions">
            <button
              type="button"
              className="liqintel-analyze-btn liqintel-analyze-btn--openai"
              onClick={() => void resolveAndAnalyze("openai")}
              disabled={disabled}
            >
              {analyzeResolving ? "Preparing..." : "Analyze with OpenAI"}
            </button>
            <button
              type="button"
              className="liqintel-analyze-btn liqintel-analyze-btn--perplexity"
              onClick={() => void resolveAndAnalyze("perplexity")}
              disabled={disabled}
            >
              {analyzeResolving ? "Preparing..." : "Analyze with Perplexity"}
            </button>
            <button
              type="button"
              className="liqintel-analyze-btn liqintel-analyze-btn--gemini"
              onClick={() => void resolveAndAnalyze("gemini")}
              disabled={disabled}
            >
              {analyzeResolving ? "Preparing..." : "Analyze with Gemini"}
            </button>
          </div>
        </div>

        <section className="liqintel-recently-viewed" aria-label="Recently viewed">
          <h2 className="liqintel-recently-viewed-title">Recently Viewed</h2>
          {recentlyViewed.length === 0 ? (
            <p className="liqintel-recently-viewed-empty">
              Reports you open will appear here.
            </p>
          ) : (
            <ul className="liqintel-recently-viewed-list">
              {recentlyViewed.map((item) => {
                const certPart =
                  item.cert !== undefined
                    ? ` · CERT ${item.cert}`
                    : "";
                return (
                  <li key={`${item.company}-${item.cert ?? "na"}-${item.viewedAt}`}>
                    <button
                      type="button"
                      className="liqintel-recently-viewed-item"
                      onClick={() => openReport(item.provider, item.company, item.cert)}
                    >
                      <span className="liqintel-recently-viewed-company">
                        {item.company}
                        {certPart}
                      </span>
                      <span className="liqintel-recently-viewed-provider">
                        {item.provider}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </header>

      <Dialog
        open={bankDialogOpen}
        onClose={() => setBankDialogOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="liqintel-bank-dialog-title"
      >
        <DialogTitle id="liqintel-bank-dialog-title">
          {pendingAnalyzeProvider
            ? "Select bank to continue analysis"
            : "US bank name search"}
        </DialogTitle>
        <DialogContent dividers className="liqintel-bank-dialog-content">
          {bankLoading ? (
            <div className="liqintel-bank-dialog-loading">
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Searching banks…
              </Typography>
            </div>
          ) : bankError ? (
            <Typography color="error">{bankError}</Typography>
          ) : bankRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No banks matched &quot;{companyName.trim()}&quot;. Try a different phrase.
            </Typography>
          ) : (
            <TableContainer className="liqintel-bank-table-wrap">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Bank name</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell align="right">CERT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bankRows.map((row, idx) => (
                    <TableRow
                      key={`${row.cert}-${idx}-${row.name}`}
                      hover
                      className="liqintel-bank-table-row"
                      onClick={() => selectBank(row)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          selectBank(row);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Select ${row.name}`}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell>{row.state}</TableCell>
                      <TableCell align="right">{row.cert}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBankDialogOpen(false);
              setPendingAnalyzeProvider(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default LiqIntel;
